/**
 * DUEL-RING soft target mark: in a bout a stepped pixel ring lies on the ground under the locked foe (else the other
 * fighter). FX pixel rules: 1-px steps on the scene grid, 1-px dark outline, master palette (gold over ink), the dashes
 * turn in whole steps, no glow. Replaces the smooth lock arcs while a bout runs (lockMarker.ts skips them).
 */
import { duelMatchOf } from '@nine/shared';
import { registerRenderPass } from './registry';
import { lockMark } from './lockMarker';

const GOLD = '#f4d08c';
const INK = '#2a1a0a';
const N = 24; // dash slots round the ring

/** The ring's points (art pixels), 2:1 iso. */
export function ringPoints(rx: number, ry: number): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    out.push([Math.round(Math.cos(a) * rx), Math.round(Math.sin(a) * ry)]);
  }
  return out;
}

registerRenderPass({
  id: 'duelFoeMark',
  layer: 'ground',
  order: 229,
  draw({ g, world: w, iso, ox, oy }) {
    const m = duelMatchOf(w)?.current;
    if (!m) return;
    const id = lockMark.id >= 0 ? lockMark.id : m.meId === w.player.id ? m.foeId : m.meId;
    const e = w.get(id);
    if (!e || e.dead) return;
    const [x, y] = iso.proj(e.x, e.y);
    const cx = Math.round(x + ox);
    const cy = Math.round(y + oy);
    const k = Math.max(1, e.scale);
    const pts = ringPoints(Math.round(15 * k), Math.round(7.5 * k));
    const phase = Math.floor(lockMark.t * 6) % 4; // whole steps, 6 a second
    g.save();
    g.imageSmoothingEnabled = false;
    // A dark inner band (a full ring, one step inside) so the gold reads on sand and snow alike (QA DUEL-P1-R1 P2).
    const inner = ringPoints(Math.round(15 * k) - 2, Math.round(7.5 * k) - 1);
    g.fillStyle = 'rgba(42,26,10,.55)';
    for (const [px, py] of inner) g.fillRect(cx + px, cy + py, 2, 1);
    // Dashes: 2 on, 2 off round the ring; shifted a slot at a time.
    const on = (i: number): boolean => ((i + phase) & 3) < 2;
    g.fillStyle = INK;
    pts.forEach(([px, py], i) => {
      if (on(i)) {
        g.fillRect(cx + px - 1, cy + py, 4, 1);
        g.fillRect(cx + px - 1, cy + py + 1, 4, 1);
        g.fillRect(cx + px, cy + py - 1, 1, 3);
      }
    });
    g.fillStyle = GOLD;
    pts.forEach(([px, py], i) => {
      if (on(i)) g.fillRect(cx + px, cy + py, 2, 1);
    });
    g.restore();
  },
});
